import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Order from "../../APIController/Order";
import { LoadingCircle } from "../../Components/LoadingCircle";
import ScrollWrapper from "../../Components/ScrollWrapper";
import PDFExporter from "../../Components/PDFExporter";

const Bonview = () => {
    const { workspaceId, offlineId } = useParams();
    const { getReciept } = Order();
    const { exportToPDF } = PDFExporter();
    const [orderData, setOrderData] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    const fetchRecieptData = async () => {
        try {
            const data = await getReciept(workspaceId, offlineId);
            if (data) setOrderData(data);
            else console.error("Failed to fetch reciept data");
        } catch (error) {
            console.error("Error fetching reciept data:", error);
        }
    };

    useEffect(() => { fetchRecieptData(); }, []);

    const handleExport = async () => {
        if (isExporting) return;
        setIsExporting(true);
        await exportToPDF(
            "bonContent",
            `bon-${orderData.id}-${orderData.offlineId}.pdf`
        );
        setIsExporting(false);
    };

    const formatDate = (iso) => {
        if (!iso) return "—";
        const d = new Date(iso);
        return d.toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" });
    };

    const typeLabel = { ON_SPOT: "Vor Ort", PREORDER: "Vorbestellung", TAKE_AWAY: "Take Away" };
    const paymentLabel = { ON_COUNTER: "An der Kasse", CARD: "Karte", PAYED_INSIDE: "Bereits bezahlt", TWINT: "Twint", CASH: "Barzahlung" };

    return (
        <div className="w-full h-full flex flex-col justify-center items-center bg-white">
            <ScrollWrapper>
                {orderData ? (
                    <div className="flex flex-col items-center gap-4">

                        {/* Quittung */}
                        <div id="bonContent" className="w-72 font-mono text-sm text-black p-4 space-y-2 bg-white">

                            <div className="text-center space-y-1">
                                <p className="text-lg font-bold tracking-widest">QUITTUNG</p>
                                <p className="text-xs text-gray-500">Bestellung #{orderData.id}</p>
                                <p className="text-xs text-gray-500">{formatDate(orderData.startTime)}</p>
                            </div>

                            <hr className="border-dashed border-gray-400" />

                            <div className="text-xs space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Typ</span>
                                    <span>{typeLabel[orderData.type] ?? orderData.type}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Zahlung</span>
                                    <span>{paymentLabel[orderData.paymentType] ?? orderData.paymentType}</span>
                                </div>
                                {orderData.comment && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Kommentar</span>
                                        <span className="text-right max-w-[60%]">{orderData.comment}</span>
                                    </div>
                                )}
                            </div>

                            <hr className="border-dashed border-gray-400" />

                            <div className="space-y-2">
                                {orderData.orderProducts.map((product) => (
                                    <div key={product.productId}>
                                        <div className="flex justify-between font-semibold">
                                            <span>{product.count}x {product.name}</span>
                                            <span>CHF {(product.price * product.count).toFixed(2)}</span>
                                        </div>
                                        {product.extras.map((extra) => (
                                            <div key={extra.id} className="flex justify-between text-xs text-gray-500 pl-3">
                                                <span>+ {extra.name}</span>
                                                <span>CHF {extra.price.toFixed(2)}</span>
                                            </div>
                                        ))}
                                        {product.mwst && (
                                            <p className="text-xs text-gray-400 pl-3">MwSt. {product.mwst}%</p>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {orderData.coupons?.length > 0 && (
                                <>
                                    <hr className="border-dashed border-gray-400" />
                                    <div className="space-y-2">
                                        <p className="text-xs font-bold tracking-wider text-gray-500">GUTSCHEINE</p>
                                        {orderData.coupons.map((coupon) => (
                                            <div key={coupon.id} className="space-y-1">
                                                <div className="flex justify-between text-xs">
                                                    <span className="font-mono text-gray-500">{coupon.offerCode}</span>
                                                    <span className="text-green-600 font-semibold">- CHF {coupon.offerPrice.toFixed(2)}</span>
                                                </div>
                                                {coupon.offerSnapshotProducts.map((p) => (
                                                    <div key={p.productId} className="flex justify-between text-xs text-gray-400 pl-3">
                                                        <span>{p.count}x {p.name}</span>
                                                        <span>CHF {p.price.toFixed(2)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            <hr className="border-dashed border-gray-400" />

                            <div className="flex justify-between font-bold text-base">
                                <span>TOTAL</span>
                                <span>CHF {orderData.totalPrice.toFixed(2)}</span>
                            </div>

                            <div className="text-center pt-1">
                                <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                                    orderData.status === "PAID"
                                        ? "bg-green-100 text-green-700"
                                        : orderData.status === "PENDING"
                                        ? "bg-yellow-100 text-yellow-700"
                                        : "bg-gray-100 text-gray-500"
                                }`}>
                                    {orderData.status}
                                </span>
                            </div>

                            <hr className="border-dashed border-gray-400" />
                            <p className="text-center text-xs text-gray-400">Danke für Ihren Besuch!</p>
                        </div>

                        {/* PDF Button */}
                        <button
                            onClick={handleExport}
                            disabled={isExporting}
                            className="w-72 py-2 rounded-lg bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                            {isExporting ? "Exportiere..." : "Als PDF herunterladen"}
                        </button>

                    </div>
                ) : (
                    <LoadingCircle />
                )}
            </ScrollWrapper>
        </div>
    );
};

export default Bonview;